"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import { cn } from "@/lib/utils";
import { PageFAQSchema } from "@/sanity/pages/PageFAQTemplate";
import FAQAccordionBlock from "./FAQAccordionBlock";
import FAQGlossaryBlock from "./FAQGlossaryBlock";
import { PillTabs } from "@/components/pageComponents/global/PillTabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn/ui/select";

export default function FAQContentSection({ data }: { data: PageFAQSchema }) {
  const [activeGroup, setActiveGroup] = useState<string>(
    data.faqGroups?.[0]?._key ?? "",
  );
  const [selectedAsset, setSelectedAsset] = useState<string>(
    () => data.faqGroups?.find((g) => g.asset)?.asset ?? "all",
  );
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const uniqueAssets = useMemo(() => {
    const assets = (data.faqGroups ?? [])
      .map((g) => g.asset)
      .filter((a): a is string => Boolean(a));
    return [...new Set(assets)];
  }, [data.faqGroups]);

  const filteredGroups = useMemo(() => {
    if (selectedAsset === "all" || uniqueAssets.length === 0)
      return data.faqGroups ?? [];
    return (data.faqGroups ?? []).filter(
      (g) => !g.asset || g.asset === selectedAsset,
    );
  }, [data.faqGroups, selectedAsset, uniqueAssets]);

  const displayGroups = useMemo(() => {
    if (!searchQuery.trim()) return filteredGroups;
    const q = searchQuery.toLowerCase();
    return filteredGroups
      .map((group) => ({
        ...group,
        items: (group.items ?? []).filter((item) =>
          item.question?.toLowerCase().includes(q),
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [filteredGroups, searchQuery]);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    displayGroups.forEach((group) => {
      if (!group._key) return;
      const el = sectionRefs.current[group._key];
      if (!el) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveGroup(group._key!);
        },
        { rootMargin: "-20% 0px -60% 0px", threshold: 0 },
      );

      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [displayGroups]);

  const handleNavClick = (key: string) => {
    const el = sectionRefs.current[key];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!data.faqGroups?.length) return null;

  return (
    <section className="w-full pb-[8rem] lg:pb-[15rem] relative z-10">
      {/* Search bar */}
      <div className="mx-auto w-[min(98.5rem,100%)] lg:mb-[6rem] px-[2rem] lg:px-0">
        <div className="flex items-center bg-white border border-[#e5e5e5] h-[9.125rem] px-[3rem] gap-[2rem]">
          <svg
            className="flex-shrink-0 w-[3rem] h-[3rem] text-black/40"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="flex-1 bg-transparent outline-none border-none text-[2rem] font-geist font-normal text-black placeholder:text-black/50 leading-[1.5] min-w-0"
            placeholder="Search for your question"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setSearchInput("");
                setSearchQuery("");
              }
            }}
          />
          <button
            onClick={() => setSearchQuery(searchInput)}
            className="flex-shrink-0 h-[5.75rem] w-[13.375rem] bg-black rounded-[0.25rem] text-white font-geist font-semibold text-[1.75rem] tracking-[-0.035rem] capitalize hover:opacity-85 transition-opacity"
          >
            Search
          </button>
        </div>
      </div>

      {data.showAssetFilter !== false && uniqueAssets.length >= 2 && (
        <div className="flex justify-center mb-[4rem] lg:mb-[6rem]">
          <PillTabs
            options={uniqueAssets.map((asset) => ({
              value: asset,
              label: asset,
            }))}
            value={selectedAsset}
            onChange={(v) => {
              setSelectedAsset(v);
              setActiveGroup(
                (data.faqGroups ?? []).find((g) => !g.asset || g.asset === v)
                  ?._key ?? "",
              );
            }}
            layoutId="faq-asset-tab"
            className="font-geist"
          />
        </div>
      )}
      <div className="mx-auto w-[min(170.5rem,calc(100%-4rem))] lg:w-[min(170.5rem,calc(100%-8rem))] flex flex-col lg:flex-row items-start lg:bg-white lg:border lg:border-[#e5e5e5] lg:gap-[10rem] lg:px-[9.75rem]">
        {/* Sidebar */}
        <aside className="flex flex-col gap-[3rem] lg:gap-[5rem] w-full lg:w-[34.75rem] flex-shrink-0 lg:sticky lg:top-[12rem] pt-[5rem] lg:pt-[9.75rem] pb-[5rem] lg:pb-[12rem]">
          {/* Asset filter */}
          {data.showAssetFilter !== false && uniqueAssets.length >= 2 && (
            <div className="flex flex-col gap-[1.5rem]">
              <p className="mb-0 uppercase text-[1.5rem] tracking-[0.255rem] font-medium text-black leading-[1.01]">
                Asset
              </p>
              <Select value={selectedAsset} onValueChange={setSelectedAsset}>
                <SelectTrigger className="w-full h-[6.75rem] px-[2rem] bg-white border border-[#e5e5e5] rounded-none shadow-none text-[2.25rem] font-medium font-geist tracking-[-0.0675rem] leading-[1.01] text-black focus:ring-0 focus:ring-offset-0 focus:outline-none [&>svg]:w-[1.375rem] [&>svg]:h-[0.75rem] [&>svg]:opacity-100 [&>svg]:shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none border border-[#e5e5e5] shadow-none p-0 font-geist">
                  <SelectItem
                    value="all"
                    className="text-[2.25rem] font-medium font-geist tracking-[-0.0675rem] leading-[1.01] px-[2rem] py-[1.75rem] rounded-none cursor-pointer focus:bg-[#f5f5f5] focus:text-black"
                  >
                    All Assets
                  </SelectItem>
                  {uniqueAssets.map((asset) => (
                    <SelectItem
                      key={asset}
                      value={asset}
                      className="text-[2.25rem] font-medium font-geist tracking-[-0.0675rem] leading-[1.01] px-[2rem] py-[1.75rem] rounded-none cursor-pointer focus:bg-[#f5f5f5] focus:text-black"
                    >
                      {asset}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Jump to nav */}
          <div className="flex flex-col gap-[3.75rem]">
            <p className="mb-0 uppercase text-[1.5rem] tracking-[0.255rem] font-medium text-black leading-[1.01] hidden lg:block">
              Jump to
            </p>
            <nav className="flex flex-col gap-[2.75rem] border border-[#e5e5e5] p-[3rem] lg:border-0 lg:p-0">
              {displayGroups.map((group) => (
                <button
                  key={group._key}
                  onClick={() => group._key && handleNavClick(group._key)}
                  className={cn(
                    "text-left text-[2.25rem] leading-[1.01] tracking-[-0.0675rem] transition-all duration-200 font-geist",
                    activeGroup === group._key
                      ? "font-medium text-black opacity-100"
                      : "font-normal text-black opacity-60 hover:opacity-80",
                  )}
                >
                  {group.categoryTitle}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* FAQ groups */}
        <div className="flex-1 w-full flex flex-col lg:border-l border-black/10 lg:pl-[10rem]">
          {displayGroups.length > 0 ? (
            displayGroups.map((group) => (
              <div
                className="py-[5rem] lg:py-[12rem] relative"
                key={group._key}
                ref={(el) => {
                  if (group._key) sectionRefs.current[group._key] = el;
                }}
              >
                {group.displayStyle === "glossary" ? (
                  <FAQGlossaryBlock group={group} />
                ) : (
                  <FAQAccordionBlock group={group} />
                )}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full lg:w-[calc(100%+19.75rem)] h-[1px] bg-black/10" />
              </div>
            ))
          ) : (
            <div className="py-[12rem] flex items-center justify-center">
              <p className="text-[2.25rem] font-geist text-black/40 font-normal leading-[1.5]">
                No results found for &ldquo;{searchQuery}&rdquo;
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
